import { useTranslation } from 'react-i18next';
import validator from 'validator';
import { clean, validate } from 'rut.js';
import { isValidNumber } from 'libphonenumber-js';
import { useMemo } from 'react';

export const useValidations = () => {
  const { t } = useTranslation();
  const prefixValidations = 'common.validations';
  const msgValidations = useMemo(() => {
    return {
      required: t(`${prefixValidations}.required`),
      invalidPhone: t(`${prefixValidations}.invalidPhone`),
      invalidEmail: t(`${prefixValidations}.invalidEmail`),
      invalidRut: t(`${prefixValidations}.invalidRut`),
      invalidBeforeDate: t(`${prefixValidations}.invalidBeforeDate`),
      invalidAlphaNumeric: t(`${prefixValidations}.invalidAlphanumeric`),
      invalidPassword: t(`${prefixValidations}.invalidPassword`),
      invalidRepeatPassword: t(`${prefixValidations}.invalidRepeatPassword`),
      invalidMinLength: (length: number) =>
        t(`${prefixValidations}.minLength`, { length: length }),
      invalidMaxLength: (length: number) =>
        t(`${prefixValidations}.maxLength`, {
          length: length,
        }),
      invalidIntNumeric: t(`${prefixValidations}.invalidIntNumeric`),
      inValidBeforeModule: t(`${prefixValidations}.invalidBeforeModule`),
    };
  }, [t]);

  /**
   * Valida el formato de la contraseña
   * @param password
   * @returns
   */
  const validatePassword = (password: string) => {
    return password.length >= 8 ? undefined : msgValidations.invalidPassword;
  };

  /**
   * Toma un valor, comprueba si es un archivo y, si lo es, comprueba si su tamaño es superior a 10485760
   * bytes (10 megabytes). Si es así, devuelve un mensaje de error, de lo contrario, devuelve indefinido
   * @param {any} value - El valor del campo.
   * @returns una cadena si el tamaño del archivo es superior a 10485760 bytes.
   */
  const validateFileSize = (value: any) => {
    if ((value as File)?.size >= 10485760) {
      return 'el archivo no puede superar los 10 megas';
    }
    return undefined;
  };

  /**
   * Esta funcion retorna otra funcion de validacion que retorna si la password pasada por parametros es igual a la contraseña repetida
   * @param password
   * @returns
   */
  const validateRepeatPassword =
    (password: string) => (repeatPassword: string) => {
      return password === repeatPassword
        ? undefined
        : msgValidations.invalidRepeatPassword;
    };

  /**
   * Valida que telefono tenga formato +XXXXXXXXXXX
   * @param phone
   * @returns
   */
  const validatePhone = (phone: string | undefined) => {
    if (phone && phone !== '') {
      const cleanPhone = phone.replaceAll(' ', '');
      if (!isValidNumber(cleanPhone)) {
        return msgValidations.invalidPhone;
      }
    }
    return undefined;
  };

  /**
   * Valida que el texto ingresado tenga formato de correo
   * @param email
   * @returns
   */
  const validateEmail = (email: any) => {
    if (email && typeof email === 'string' && !validator.isEmail(email)) {
      return msgValidations.invalidEmail;
    }
    return undefined;
  };

  /**
   * Función que valida que el texto ingresado no sea vacío
   * y que no contenga sólo espacios.
   * @param value
   * @returns
   */
  const validateTextNotEmpty = (value: any) => {
    if (value === null || value === undefined) {
      return msgValidations.required;
    }

    if (typeof value === 'string' && value.trim().length === 0) {
      return msgValidations.required;
    }

    return undefined;
  };

  /**
   * Función que valida que el texto ingresado sea un rut válido
   * @param value
   * @returns
   */
  const validateRut = (value: any) => {
    if (value && typeof value === 'string' && !validate(clean(value))) {
      return msgValidations.invalidRut;
    }
    return undefined;
  };

  /**
   * Función que valida que el texto solo tenga números y letras
   * @param value
   * @returns
   */
  const validateAlphaNumeric = (value: string | undefined) => {
    if (!value || !/^[a-zA-Z0-9]+$/.test(value)) {
      return;
    }
    return undefined;
  };

  /**
   * Función que valida que la fecha de fin sea mayor que la de inicio
   * @param endDate
   * @returns
   */
  const validateBeforeDate =
    (endDate?: string) => (value: string | undefined) => {
      if (value && endDate) {
        if (Date.parse(endDate) < Date.parse(value)) {
          return msgValidations.invalidBeforeDate;
        }
      }
      return undefined;
    };

  /**
   * Recibe un numero de caracteres y retorna otra funcion que valida la longitud minima del string ingresado
   * @param minLength
   * @returns
   */
  const validateMinLength = (minLength: number) => {
    return (value?: any) =>
      value && typeof value === 'string' && value?.length < minLength
        ? msgValidations.invalidMinLength(minLength)
        : undefined;
  };

  /**
   * Retorna los valores necesario para agregar un pattern en rules
   * para validar que el valor ingresado sea numérico
   */
  const rulesPatternNumeric = useMemo(() => {
    return {
      value: /^\d*$/,
      message: msgValidations.invalidIntNumeric,
    };
  }, [msgValidations]);

  return {
    rulesPatternNumeric,
    validatePhone,
    validateEmail,
    validateTextNotEmpty,
    validateRut,
    validatePassword,
    validateRepeatPassword,
    msgValidations,
    validateBeforeDate,
    validateAlphaNumeric,
    validateMinLength,
    validateFileSize,
  };
};
